import { useEffect, useState } from "react";
import { Plus, UserPlus, Trash2, Loader2, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  PageHeader, Spinner, EmptyState, Modal, Field, GoldButton, GhostButton, inputCls, PLATFORM_META,
} from "@/components/app/widgets";

const EMPTY = {
  name: "", email: "", campaign_id: "", prospect_id: "", source: "",
  platform: "", lead_magnet: "", status: "new", product_interest: "", is_demo: false,
};

export default function Leads() {
  const [items, setItems] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [convert, setConvert] = useState(null);
  const [revenue, setRevenue] = useState("");

  const load = () => api.get("/leads").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/campaigns").then((r) => setCampaigns(r.data.items)).catch(() => {});
    api.get("/prospects").then((r) => setProspects(r.data.items)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.campaign_id) delete payload.campaign_id;
      if (!payload.prospect_id) delete payload.prospect_id;
      await api.post("/leads", payload);
      toast.success("Lead captured — source attribution preserved");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doConvert = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/leads/${convert.lead_id}/convert`, { revenue: Number(revenue) || 0 });
      toast.success("Lead converted — revenue attributed");
      setConvert(null);
      setRevenue("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete lead "${l.name}"?`)) return;
    try {
      await api.delete(`/leads/${l.lead_id}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!items) return <Spinner label="Loading leads" />;

  return (
    <div data-testid="leads-page">
      <PageHeader title="Leads" sub="Partner → Campaign → Lead → Customer → Revenue. The chain is never broken.">
        <GoldButton onClick={() => setOpen(true)} data-testid="new-lead-button"><Plus className="h-4 w-4" /> Capture Lead</GoldButton>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState
          icon={UserPlus}
          title="No leads yet"
          desc="Leads captured through partnerships land here with full source attribution."
          action={<GoldButton onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Capture Lead</GoldButton>}
        />
      ) : (
        <div className="overflow-x-auto border border-[#27272A]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-5 py-3">Lead</th>
                <th className="px-5 py-3">Platform</th>
                <th className="px-5 py-3">Partner</th>
                <th className="px-5 py-3">Campaign</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Revenue</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((l) => (
                <tr key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className="transition-colors hover:bg-white/[0.03]">
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-white">{l.name}</p>
                    {l.email && <p className="text-xs text-zinc-500">{l.email}</p>}
                  </td>
                  <td className="px-5 py-3.5 text-xs text-zinc-400">{PLATFORM_META[l.platform]?.label || l.platform || "—"}</td>
                  <td className="max-w-[160px] truncate px-5 py-3.5 text-xs text-zinc-400">{l.partner_name || "—"}</td>
                  <td className="max-w-[160px] truncate px-5 py-3.5 text-xs text-zinc-400">{l.campaign_name || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      l.converted ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-zinc-400"
                    }`}>
                      {l.converted ? "Customer" : l.status}
                    </span>
                    {l.is_demo && (
                      <span className="ml-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-400">
                        Demo
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right text-sm font-semibold text-[#D4AF37]">
                    {l.converted ? `$${Number(l.revenue || 0).toLocaleString()}` : "—"}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex justify-end gap-1">
                      {!l.converted && (
                        <button
                          onClick={() => setConvert(l)}
                          data-testid={`convert-lead-${l.lead_id}`}
                          className="flex items-center gap-1 rounded-full border border-[#D4AF37]/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        >
                          <DollarSign className="h-3 w-3" /> Convert
                        </button>
                      )}
                      <button onClick={() => remove(l)} data-testid={`delete-lead-${l.lead_id}`} className="p-1.5 text-zinc-600 hover:text-red-400">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Capture Lead" wide>
        <form onSubmit={save} data-testid="lead-form" className="grid gap-4 sm:grid-cols-2">
          <Field label="Name"><input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" className={inputCls} /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" className={inputCls} /></Field>
          <Field label="Campaign">
            <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value, prospect_id: "" })} data-testid="lead-campaign-select" className={inputCls}>
              <option value="">—</option>
              {campaigns.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Partner (prospect)">
            <select value={form.prospect_id} onChange={(e) => setForm({ ...form, prospect_id: e.target.value })} data-testid="lead-partner-select" className={inputCls}>
              <option value="">—</option>
              {prospects.filter((x) => !form.campaign_id || x.campaign_id === form.campaign_id).map((x) => (
                <option key={x.prospect_id} value={x.prospect_id}>{x.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Platform">
            <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="lead-platform-select" className={inputCls}>
              <option value="">—</option>
              {Object.entries(PLATFORM_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Source detail"><input type="text" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. FB Group — DELF B2 Candidates" data-testid="lead-source-input" className={inputCls} /></Field>
          <Field label="Lead magnet"><input type="text" value={form.lead_magnet} onChange={(e) => setForm({ ...form, lead_magnet: e.target.value })} data-testid="lead-magnet-input" className={inputCls} /></Field>
          <Field label="Product interest"><input type="text" value={form.product_interest} onChange={(e) => setForm({ ...form, product_interest: e.target.value })} data-testid="lead-interest-input" className={inputCls} /></Field>
          <label className="flex items-center gap-3 border border-white/10 px-4 py-3 sm:col-span-2" data-testid="lead-demo-toggle">
            <input
              type="checkbox" checked={form.is_demo}
              onChange={(e) => setForm({ ...form, is_demo: e.target.checked })}
              data-testid="lead-demo-checkbox"
              className="h-4 w-4 accent-[#D4AF37]"
            />
            <span className="text-xs text-zinc-400">
              Mark as <span className="font-bold text-amber-400">DEMO / TEST</span> data — excluded from real analytics
            </span>
          </label>
          <div className="flex justify-end gap-3 sm:col-span-2">
            <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
            <GoldButton type="submit" disabled={saving} data-testid="lead-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Capture Lead"}
            </GoldButton>
          </div>
        </form>
      </Modal>

      <Modal open={!!convert} onClose={() => setConvert(null)} title={`Convert ${convert?.name || ""} to Customer`}>
        <form onSubmit={doConvert} data-testid="convert-form" className="space-y-4">
          <Field label="Revenue ($)">
            <input required type="number" min="0" step="0.01" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="convert-revenue-input" className={inputCls} placeholder="147" />
          </Field>
          <div className="flex justify-end gap-3">
            <GhostButton type="button" onClick={() => setConvert(null)}>Cancel</GhostButton>
            <GoldButton type="submit" data-testid="convert-submit-button">Record Sale</GoldButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
